import { useMutation, useQueries, useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { useMemo } from "react";
import { ZodError, z } from "zod";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { statusApiSchema } from "../schemas/status";
import { paginatedResponse } from "../utils";

const ProductTagSchema = z.object({
    id: z.string(),
    tag_name: z.string(),
});

const CreateProductTagRequestSchema = z.object({
    tag_name: z.string(),
});

const ProductTypeSchema = z.object({
    id: z.string(),
    name: z.string(),
    name_eng: z.string(),
    no_footage: z.boolean(),
});

const ProductKindSchema = z.object({
    id: z.string(),
    name: z.string(),
    name_eng: z.string(),
    key: z.string(),
});

const ShortProductSchema = z.object({
    id: z.string(),
    name: z.string(),
});

export const InvestmentSchema = z.object({
    id: z.string(),
    id_invest_home: z.string(),
    investment_name: z.string(),
    investment_name_sales: z.string(),
    invest_booking_duration_days: z.number(),
    invest_booking_reschedule_days: z.number(),
    invest_keywords: z.string(),
});

export const GetProductsResponseEntrySchema = z.object({
    id: z.string(),
    name_full: z.string(),
    name_short: z.string(),
    blocked_for_sale: z.boolean(),
    footage: z.number().nullable(),
    footage_postconstruction: z.number().nullable(),
    tags: z.array(
        z.object({
            id: z.string(),
            tag_name: z.string(),
        }),
    ),
    style: z.object({
        id: z.string(),
        style_name: z.string(),
    }).optional().nullable(),
    note: z.string(),
    hotel_note: z.string(),
    product_number: z.string(),
    investment: InvestmentSchema,
    type: ProductTypeSchema.nullable(),
    kind: ProductKindSchema.nullable(),
    status: statusApiSchema.status,
    pricing: z
        .object({
            listed_net_per_m2: z.string(),
            base_net_per_m2: z.string(),
            min_net_per_m2: z.string(),
            vat_proc: z.coerce.number(),
            discount_max: z.coerce.number(),
            pricing_published: z.boolean().optional(),
        })
        .nullable()
        .transform(
            (val) =>
                val ?? {
                    listed_net_per_m2: "0",
                    base_net_per_m2: "0",
                    min_net_per_m2: "0",
                    vat_proc: "0",
                    discount_max: "0",
                    pricing_published: false,
                },
        ),
    num_rooms: z.number(),
    orientation: z.object({
        north: z.boolean(),
        south: z.boolean(),
        east: z.boolean(),
        west: z.boolean(),
    }),
    flat_orientation: z.array(z.string()).nullable().default([]),
    floor: z.number(),
    children: z.array(ShortProductSchema).nullable().default([]),
    parent_product: ShortProductSchema.nullable().default(null),
    is_apartment: z.boolean(),
    card_pdf: z.object({
        id: z.string(),
        name: z.string().nullable(),
        content_type: z.string().nullable(),
        miniature_download_link: z.string().nullable(),
    }).nullable().default(null),
    product_final_settlement: z.boolean().nullable().optional(),
});

const InvestmentUpdateSchema = z.object({
    id: z.string(),
    invest_booking_duration_days: z.number(),
    invest_booking_reschedule_days: z.number(),
});

const ProductsMassUpdateXlsxResponseSchema = z.object({
    products: z.array(z.string()),
});

const ProductUpdateSchema = z.object({
    name_full: z.string().optional(),
    name_short: z.string().optional(),
    blocked_for_sale: z.boolean().optional(),
    footage: z.number().optional(),
    footage_postconstruction: z.number().nullable().optional(),
    note: z.string().optional(),
    floor: z.number().optional(),
    product_number: z.string().optional(),
    num_rooms: z.number().optional(),
    pricing: z
        .object({
            listed_net_per_m2: z.string().optional(),
            base_net_per_m2: z.string().optional(),
            min_net_per_m2: z.string().optional(),
            vat_proc: z.coerce.number().optional(),
            discount_max: z.coerce.number().optional(),
        })
        .optional(),
    investment: z.string().optional(),
    type: z.string().optional(),
    kind: z.string().optional(),
    status: z.string().optional(),
    orientation: z
        .object({
            north: z.boolean().optional(),
            south: z.boolean().optional(),
            east: z.boolean().optional(),
            west: z.boolean().optional(),
        })
        .optional(),
    tags: z.array(z.string()).optional(),
    children: z.array(z.string()).optional(),
});

const ProductMassUpdateRequestSchema = z.object({
    products: z.array(z.string()),
    update_data: ProductUpdateSchema,
});

const CreateProductTypeRequestSchema = z.object({
    name: z.string(),
    name_eng: z.string(),
    no_footage: z.boolean(),
});

export const productsApiSchema = {
    getProductsResponse: paginatedResponse(GetProductsResponseEntrySchema),
    product: GetProductsResponseEntrySchema,
    getProductsBookedDaysResponse: z.object({
        booked_days: z.array(z.string()),
    }),
    getInvestmentsResponse: paginatedResponse(InvestmentSchema),
    getInvestmentsSelectListResponse: InvestmentSchema,
    shortProduct: ShortProductSchema,
    productType: ProductTypeSchema,
};

export const productsApi = {
    getProducts: async (axios, params) => {
        return axios
            .get(apiUrls.products.all, { params })
            .then((response) => productsApiSchema.getProductsResponse.parse(response.data));
    },

    getApartments: async (axios, params) => {
        return axios
            .get(apiUrls.products.apartments, { params })
            .then((response) => productsApiSchema.getProductsResponse.parse(response.data));
    },

    getAdjacent: async (axios, params) => {
        return axios
            .get(apiUrls.products.adjacent, { params })
            .then((response) => productsApiSchema.getProductsResponse.parse(response.data));
    },

    getProduct: async (axios, productId) => {
        const params = new URLSearchParams();
        params.set("id", productId);
        return axios
            .get(apiUrls.products.all, { params })
            .then((response) => productsApiSchema.product.parse(response.data.results[0]));
    },

    updateProduct: async (axios, productId, body) => {
        return axios
            .put(apiUrls.products.update(productId), body)
            .then((response) => response.data);
    },

    publishLastProductPricing: async (axios, productId) => {
        return axios
            .post(apiUrls.products.publishLastProductPricing(productId))
            .then((response) => response.data);
    },

    getProductsBookedDays: (axios, productsIds, excludedBookings) => {
        return axios
            .post(apiUrls.products.bookedDays, { products: productsIds, excluded_bookings: excludedBookings })
            .then((response) =>
                productsApiSchema.getProductsBookedDaysResponse.parse(response.data),
            );
    },

    getTypes: async (axios, params) => {
        return axios.get(apiUrls.products.types.list, { params }).then((response) => response.data);
    },

    getStatuses: async (axios, params) => {
        return axios.get(apiUrls.products.statuses, { params }).then((response) => response.data);
    },

    getKinds: async (axios, params) => {
        return axios.get(apiUrls.products.kinds, { params }).then((response) => response.data);
    },

    massUpdate: async (axios, body) => {
        return axios.post(apiUrls.products.massUpdate, body).then((response) => response.data);
    },

    massUpdateXlsx(axios, { file }) {
        const formData = new FormData();
        formData.append("file", file);
        return axios
            .post(apiUrls.products.massUpdateXlsx, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => response.data);
    },

    getAllTags: async (axios, params) => {
        return axios.get(apiUrls.products.tags.all, { params }).then((response) => response.data);
    },

    createTag: async (axios, body) => {
        return axios.post(apiUrls.products.tags.create, body).then((response) => response.data);
    },

    getInvestments: async (axios) => {
        const params = new URLSearchParams({ page_size: "100" });
        return axios
            .get(apiUrls.investments.all, { params })
            .then((response) => productsApiSchema.getInvestmentsResponse.parse(response.data));
    },

    getInvestmentSelectList: async (axios, params) => {
        return axios
            .get(apiUrls.investments.selectList, { params })
            .then((response) => response.data);
    },

    updateInvestment: async (axios, body) => {
        const investmentId = body.id;
        return axios
            .put(apiUrls.investments.update(investmentId), body)
            .then((response) => response.data);
    },

    getTypesManagement: async (axios) => {
        return axios.get(apiUrls.products.types.list).then((response) => response.data);
    },

    createProductType: async (axios, body) => {
        return axios.post(apiUrls.products.types.create, body).then((response) => response.data);
    },

    deleteProductType: async (axios, typeId) => {
        return axios
            .delete(apiUrls.products.types.delete(typeId))
            .then((response) => response.data);
    },
    deleteProduct: async (axios, productId) => {
        return axios.delete(apiUrls.products.delete(productId)).then((response) => response.data);
    },
    getPricing: async (axios, params) => {
        return axios.get(apiUrls.products.pricing, { params }).then((response) => response.data);
    },

    getProductsWithPricing: (axios, params) => {
        return axios
            .get(apiUrls.products.productsWithPricing, {
                params,
            })
            .then((response) => response.data);
    },

    getStyleSelectList: (axios, params) => {
        return axios
            .get(apiUrls.products.styleSelectList, {
                params,
            })
            .then((response) => response.data);
    },

    getProposals: (axios, params) => {
        return axios
            .get(apiUrls.products.proposals, {
                params,
            })
            .then((response) => response.data);
    },

    getProposalFilteringData: (axios, params) => {
        return axios
            .get(apiUrls.products.proposalFilteringData, {
                params,
            })
            .then((response) => response.data);
    },

    getProposalById: (axios, proposalId) => {
        return axios.get(apiUrls.products.proposalDetails(proposalId)).then((response) => response.data);
    },

    createProposal: (axios, body) => {
        return axios.post(
            apiUrls.products.proposals,
            body,
        );
    },

    updateProposal: (axios, proposalId, body) => {
        return axios.patch(
            apiUrls.products.proposalDetails(proposalId),
            body,
        );
    },

    updatePricing: (axios, pricingId, body) => {
        return axios.patch(
            apiUrls.products.pricingDetails(pricingId),
            body,
        );
    },
};

export function useProducts(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["products", Object.fromEntries(params.entries())],
        queryFn: () => {
            return productsApi.getProducts(axiosPriv, params);
        },
    });
}

export function useProductsByIds(ids, enabled = true) {
    const axiosPriv = useAxiosPrivate();
    const queries = useQueries({
        queries: ids.map((id) => ({
            queryKey: ["product", id],
            queryFn: () => productsApi.getProduct(axiosPriv, id),
            enabled: enabled,
        })),
    });

    const isLoading = queries.some((query) => query.isLoading);
    const data = isLoading ? null : queries.map((query) => query.data).filter((p) => p != null);

    return { data, isLoading };
}

export function useApartments(params, options, excludeStatuses) {
    const axiosPriv = useAxiosPrivate();

    // Add exclude_statuses to params if provided
    const finalParams = useMemo(() => {
        const newParams = new URLSearchParams(params);
        if (excludeStatuses && excludeStatuses.length > 0) {
            newParams.set("exclude_statuses", excludeStatuses.join(","));
        }
        return newParams;
    }, [params, excludeStatuses]);

    return useQuery({
        ...options,
        queryKey: ["apartments", Object.fromEntries(finalParams.entries())],
        queryFn: () => {
            return productsApi.getApartments(axiosPriv, finalParams);
        },
    });
}

export function useAdjacentProducts(params, options, excludeStatuses) {
    const axiosPriv = useAxiosPrivate();

    // Add exclude_statuses to params if provided
    const finalParams = useMemo(() => {
        const newParams = new URLSearchParams(params);
        if (excludeStatuses && excludeStatuses.length > 0) {
            newParams.set("exclude_statuses", excludeStatuses.join(","));
        }
        return newParams;
    }, [params, excludeStatuses]);

    return useQuery({
        ...options,
        queryKey: ["adjacentProducts", Object.fromEntries(finalParams.entries())],
        queryFn: () => {
            return productsApi.getAdjacent(axiosPriv, finalParams);
        },
    });
}

export function useProductTypes(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["productTypes"],
        queryFn: () => {
            return productsApi.getTypes(axiosPriv, params);
        },
    });
}

export function useProductStatuses(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["productStatuses"],
        queryFn: () => {
            return productsApi.getStatuses(axiosPriv, params);
        },
    });
}

export function useProductKinds(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["productKinds"],
        queryFn: () => {
            return productsApi.getKinds(axiosPriv, params);
        },
    });
}

export function useGetProductsBookedDays(productsIds, excludedBookings, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["productsBookedDays", productsIds],
        queryFn: () => {
            return productsApi.getProductsBookedDays(axiosPriv, productsIds, excludedBookings);
        },
    });
}

export function useProductTags(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["productTags"],
        queryFn: () => {
            return productsApi.getAllTags(axiosPriv, params);
        },
    });
}

export function useCreateProductTagMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["productTags"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => productsApi.createTag(axiosPriv, body),
        onError: onServerError,
    });
}

export function useImportFileEditMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ file }) => {
            return productsApi.massUpdateXlsx(axiosPriv, { file });
        },
    });
}

export function useInvestments(options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["investments"],
        queryFn: () => {
            return productsApi.getInvestments(axiosPriv);
        },
    });
}

export function useInvestmentSelectList(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["investment-select_list"],
        queryFn: () => {
            return productsApi.getInvestmentSelectList(axiosPriv, params);
        },
    });
}

export function useUpdateInvestmentMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["investments"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => productsApi.updateInvestment(axiosPriv, body),
        onError: onServerError,
    });
}

export function useMassUpdateProductsMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => productsApi.massUpdate(axiosPriv, body),
    });
}

export function useUpdateProductMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ productId, body }) => productsApi.updateProduct(axiosPriv, productId, body),
        onError: onServerError,
    });
}

export function usePublishLastPricingProductMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["pricing"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ productId }) => productsApi.publishLastProductPricing(axiosPriv, productId),
        onError: onServerError,
    });
}

export function useCreateProductTypeMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["productTypesManagement"] });
            await queryClient.invalidateQueries({ queryKey: ["productTypes"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => productsApi.createProductType(axiosPriv, body),
        onError: onServerError,
    });
}

export function useDeleteProductTypeMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["productTypesManagement"] });
            await queryClient.invalidateQueries({ queryKey: ["productTypes"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ typeId }) => productsApi.deleteProductType(axiosPriv, typeId),
        onError: onServerError,
    });
}

export function useDeleteProductMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            await queryClient.invalidateQueries({ queryKey: ["apartments"] });
            await queryClient.invalidateQueries({ queryKey: ["adjacentProducts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ productId }) => productsApi.deleteProduct(axiosPriv, productId),
    });
}

export function usePricing(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["pricing", params],
        queryFn: () => {
            return productsApi.getPricing(axiosPriv, params);
        },
    });
}

export function useProductsWithPricing(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["products-with-pricing", Object.fromEntries(params.entries())],
        queryFn: () => {
            return productsApi.getProductsWithPricing(axiosPriv, params);
        },
    });
}

export function useStyleSelectList(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["style-select-list", Object.fromEntries(params.entries())],
        queryFn: () => {
            return productsApi.getStyleSelectList(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useProposals(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["proposals", Object.fromEntries(params.entries())],
        queryFn: () => {
            return productsApi.getProposals(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useProposalFilteringData(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["proposals-filtering-data", Object.fromEntries(params.entries())],
        queryFn: () => {
            return productsApi.getProposalFilteringData(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useProposalById(proposalId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["proposals", proposalId],
        queryFn: () => {
            return productsApi.getProposalById(axiosPriv, proposalId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCreateProposalMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["proposals"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ data }) => productsApi.createProposal(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateProposalMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["proposals"] });
            await queryClient.invalidateQueries({ queryKey: ["proposals", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => productsApi.updateProposal(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useUpdatePricingMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["pricing"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => productsApi.updatePricing(axiosPriv, id, data),
        onError: onServerError,
    });
}